import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d5nplqfecauet1j8b93g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          <div>
            <div className="mb-6">
              <span className="text-sm font-semibold text-[#0D9488] uppercase tracking-wide whitespace-nowrap">
                GET IN TOUCH
              </span>
            </div>

            <h2 className="text-5xl md:text-6xl font-bold text-[#1E3A8A] mb-6 leading-tight">
              Let's Discuss Your
              <br />
              Digitization Project
            </h2>

            <p className="text-gray-600 text-base leading-relaxed mb-12">
              Have questions about our services or want to start a preservation project? 
              We're here to help you protect your valuable archives for future generations.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#D97706]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-[#D97706]"></i>
                </div>
                <div>
                  <h4 className="font-bold text-[#1E3A8A] mb-1">Email Us</h4>
                  <a href="mailto:info@legacydigitization.com" className="text-gray-600 hover:text-[#D97706] transition-colors">
                    info@legacydigitization.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#D97706]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-[#D97706]"></i>
                </div>
                <div>
                  <h4 className="font-bold text-[#1E3A8A] mb-1">Call Us</h4>
                  <a href="tel:+15551234567" className="text-gray-600 hover:text-[#D97706] transition-colors">
                    +1 (555) 123-4567
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#D97706]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-[#D97706]"></i>
                </div>
                <div>
                  <h4 className="font-bold text-[#1E3A8A] mb-1">Visit Us</h4>
                  <p className="text-gray-600">
                    123 Heritage Lane<br />
                    Digital Preservation District<br />
                    Archive City, AC 12345
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#D97706]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-2xl text-[#D97706]"></i>
                </div>
                <div>
                  <h4 className="font-bold text-[#1E3A8A] mb-1">Business Hours</h4>
                  <p className="text-gray-600">
                    Monday - Friday: 9:00 AM - 6:00 PM<br />
                    Saturday: 10:00 AM - 4:00 PM<br />
                    Sunday: Closed
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#FAFAF8] rounded-3xl p-8 md:p-10">
            <h3 className="text-2xl font-bold text-[#1E3A8A] mb-6">Send Us a Message</h3>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D97706] focus:border-transparent text-sm transition-all"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D97706] focus:border-transparent text-sm transition-all"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D97706] focus:border-transparent text-sm transition-all"
                  placeholder="How can we help you?"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D97706] focus:border-transparent text-sm transition-all resize-none"
                  placeholder="Tell us about your project... (max 500 characters)"
                ></textarea>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-check-line mr-2"></i>
                  Thank you for your message! We'll get back to you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  Something went wrong. Please try again or contact us directly.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1E3A8A] text-white py-4 rounded-full font-semibold text-base hover:bg-[#1E40AF] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill"></i>
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
